/**
 * Contacts filter state, SHARED across every mount of ContactsList (the
 * full Contacts screen, the web right pane, the contacts slide-over) and
 * PERSISTED per user+business — filtering in one place is reflected in the
 * others immediately (single Zustand store, not per-component useState),
 * and survives closing/reopening the app (zustand's persist middleware,
 * same AsyncStorage-backed pattern appStore.ts already uses for auth/
 * business state).
 *
 * Scoped by `${userId}::${businessId}` so switching businesses or accounts
 * never leaks one business's remembered filters into another's view.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createContext, useCallback, useContext } from 'react';
import { useAppStore } from './appStore';
import type { ContactAnalyticalFilters } from '../components/ContactAnalyticalFiltersModal';

export type AssignmentFilterTarget = { type: 'user' | 'department' | 'unassigned'; id?: string };

export interface ContactsFilterValues {
  selectedProvider: string | null;
  assignmentFilter: { type: 'user' | 'department' | 'unassigned'; id?: string; mode?: 'include' | 'exclude' } | null;
  includeAssignmentFilters: AssignmentFilterTarget[];
  excludeAssignmentFilters: AssignmentFilterTarget[];
  includeTags: string[];
  excludeTags: string[];
  includeDataListIds: string[];
  excludeDataListIds: string[];
  filterCities: string[];
  filterCountries: string[];
  radiusKm: string;
  radiusFilterEnabled: boolean;
  sortByDistance: 'nearest' | 'farthest' | null;
  nearPoint: { latitude: number; longitude: number } | null;
  nearPointLabel: string | null;
  includeUnlocated: boolean;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  customFieldFilters: Record<string, string[]>;
  analyticalFilters: ContactAnalyticalFilters;
  /** How many contacts each "page" (initial load + each "load more") fetches —
   * remembered per user+business like every other filter here, so it doesn't
   * reset back to the default every time the user leaves and returns. */
  pageSize: number;
  /** The Inbox list's own status filter (its filters are kept apart from Contacts', see
   * ContactsFilterScopeContext). */
  inboxStatus: 'all' | 'unread' | 'active' | 'near expiry' | 'expired';
}

export const DEFAULT_CONTACTS_FILTERS: ContactsFilterValues = {
  selectedProvider: null,
  assignmentFilter: null,
  includeAssignmentFilters: [],
  excludeAssignmentFilters: [],
  includeTags: [],
  excludeTags: [],
  includeDataListIds: [],
  excludeDataListIds: [],
  filterCities: [],
  filterCountries: [],
  radiusKm: '',
  radiusFilterEnabled: false,
  sortByDistance: null,
  nearPoint: null,
  nearPointLabel: null,
  includeUnlocated: true,
  sortBy: null,
  sortOrder: 'asc',
  customFieldFilters: {},
  analyticalFilters: {},
  pageSize: 50,
  inboxStatus: 'all',
};

/** A name for a separate set of remembered filters — the Inbox's ContactsList uses 'inbox', so
 * filtering it never changes the Contacts list beside it (and the other way round). */
export const ContactsFilterScopeContext = createContext<string>('');

type Updater<T> = T | ((prev: T) => T);

interface ContactsFilterStoreState {
  byScope: Record<string, ContactsFilterValues>;
  setField: <K extends keyof ContactsFilterValues>(scopeKey: string, key: K, valueOrUpdater: Updater<ContactsFilterValues[K]>) => void;
  resetFilters: (scopeKey: string) => void;
}

// analyticalFilters carries real Date fields (orderDateFrom/orderDateTo/
// contactedDateFrom/contactedDateTo) — plain JSON.stringify/parse (zustand
// persist's default) turns a Date into an ISO string on write but never
// revives it back into a Date on read, which would silently break the
// first `.toISOString()` call on a rehydrated filter after an app
// restart. This reviver converts any ISO-8601-shaped string back to a
// real Date on read; safe because no other persisted field here is ever a
// date-formatted string (tags/city/provider names, etc. aren't).
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
const dateAwareReviver = (_key: string, value: unknown): unknown => {
  if (typeof value === 'string' && ISO_DATE_PATTERN.test(value)) {
    return new Date(value);
  }
  return value;
};

export const useContactsFilterStore = create<ContactsFilterStoreState>()(
  persist(
    (set) => ({
      byScope: {},
      setField: (scopeKey, key, valueOrUpdater) => set((state) => {
        const current = { ...DEFAULT_CONTACTS_FILTERS, ...(state.byScope[scopeKey] || {}) };
        const nextValue = typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (prev: any) => any)(current[key])
          : valueOrUpdater;
        if (nextValue === current[key]) return state;
        return {
          byScope: {
            ...state.byScope,
            [scopeKey]: { ...current, [key]: nextValue },
          },
        };
      }),
      resetFilters: (scopeKey) => set((state) => ({
        byScope: { ...state.byScope, [scopeKey]: DEFAULT_CONTACTS_FILTERS },
      })),
    }),
    {
      name: 'sendifier-contacts-filters',
      storage: createJSONStorage(() => AsyncStorage, { reviver: dateAwareReviver }),
    }
  )
);

export const useContactsFilterScopeKey = (): string => {
  const userId = useAppStore((s) => s.user?.id);
  const businessId = useAppStore((s) => s.currentBusiness?._id);
  const scope = useContext(ContactsFilterScopeContext);
  return `${userId || 'anon'}::${businessId || 'none'}${scope ? `::${scope}` : ''}`;
};

/**
 * Drop-in useState replacement for one filter field — same [value, setValue]
 * shape (setValue accepts a plain value or an updater function), backed by
 * the shared/persisted store above instead of component-local state. Swap
 * `useState(...)` for `useContactsFilterField('fieldName')` and every
 * existing read/write call site (including functional updates) keeps
 * working unchanged.
 */
export function useContactsFilterField<K extends keyof ContactsFilterValues>(
  key: K
): [ContactsFilterValues[K], (valueOrUpdater: Updater<ContactsFilterValues[K]>) => void] {
  const scopeKey = useContactsFilterScopeKey();
  const value = useContactsFilterStore((s) => ({ ...DEFAULT_CONTACTS_FILTERS, ...(s.byScope[scopeKey] || {}) })[key]);
  const setField = useContactsFilterStore((s) => s.setField);
  const setValue = useCallback(
    (valueOrUpdater: Updater<ContactsFilterValues[K]>) => setField(scopeKey, key, valueOrUpdater),
    [scopeKey, key, setField]
  );
  return [value, setValue];
}

/** Resets every field in this store back to DEFAULT_CONTACTS_FILTERS for the
 * current user+business scope, in one call — backs a single "Clear" action
 * for all filters at once, instead of each filter section's own individual
 * clear control. Deliberately leaves the free-text search query untouched
 * (it isn't part of this store — see ContactsList's own searchQuery state). */
export function useResetContactsFilters(): () => void {
  const scopeKey = useContactsFilterScopeKey();
  const resetFilters = useContactsFilterStore((s) => s.resetFilters);
  return useCallback(() => resetFilters(scopeKey), [scopeKey, resetFilters]);
}
